import { Button, Checkbox, Flex, Select, Text } from "@theorgchartdev/storybook";
import * as React from "react";
import { createSelectOptions } from "../../../../../../Utils/Misc";
import { enumATSModes } from "@/appComponents/Blocks/Modals/SettingsModal/ChartSettings/DataSourceConfiguration/DataSources/DataSourceSetupComposite";
import {
  AttachLoadedHeadersToChart,
  GetMapping
} from "@/appComponents/Blocks/Modals/SettingsModal/ChartSettings/Utils";
import { LoadHeadersRFC } from "@/Utils/Promises";
import {
  kATSConnectorsWithStaticMapping,
  kAppendDisplayFieldMappingConnectors,
  seccondaryAutoLoadConnectors,
  GetAppendDataType,
  GetAppendDisplayFields,
  DATA_SOURCES_TEST_PREFIX
} from "./Utils";
import { appcstr } from "@coreapp/utils/appcstr";
import { appstr } from "@coreapp/utils/appstr";
import { IsPositionChart } from "@coreapp/utils/IsPositionChart";

// The two mapping Selects below serve a different field pair per flow (import/append vs. merge, employee
// vs. position dataset), so the testid tracks the resolved field key -- one Pendo feature per real control.
const FIELD_SELECT_SLUGS = {
  PersonID: "person-id",
  SupervisorID: "supervisor-id",
  recordField: "record-field",
  mergeField: "merge-field",
  PositionID: "position-id",
  "Parent PositionID": "parent-position-id",
  EmployeeID: "employee-id",
  "Employee SupervisorID": "employee-supervisor-id",
  "Employee PositionID": "employee-position-id",
  Name: "name",
  Title: "title",
  "Position Title": "position-title"
};

const fieldSelectTestId = fieldKey =>
  `${DATA_SOURCES_TEST_PREFIX}-${FIELD_SELECT_SLUGS[fieldKey] ?? "field"}-select`;

export const GetSourceMap = (curChart, config, mapIDKey) => {
  if (!config["map"]) config["map"] = {};
  if (!config["map"][mapIDKey]) {
    if (curChart["fields"] && curChart["fields"][mapIDKey])
      config["map"][mapIDKey] = curChart["fields"][mapIDKey];
    else config["map"][mapIDKey] = "";
  }
  return config["map"][mapIDKey];
};

const getFieldKeysForPositionChart = (config, importType) => {
  if (!["append", "merge"].includes(importType)) {
    return null;
  }

  const dataType = importType === "append" ? GetAppendDataType(config) : config?.dataType;

  if (dataType === "positions") {
    return {
      idKey: "PositionID",
      superIDkey: "Parent PositionID",
      fieldsToRemove: ["EmployeeID", "Employee SupervisorID", "Employee PositionID"]
    };
  } else if (dataType === "employees") {
    return {
      idKey: "EmployeeID",
      superIDkey: "Employee PositionID",
      fieldsToRemove: ["PositionID", "Parent PositionID"]
    };
  }

  return null;
};

export const MergeDupesSection = ({ config, headers, onChange, id = "toggle-merge-dupes" }) => {
  const effectiveMergeDupes = !!config.mergeDupes;
  const effectiveMergeByField = config.mergeByField;
  const options = createSelectOptions(headers, header => onChange({ mergeByField: header }));

  return (
    <>
      <Checkbox
        id={id}
        data-testid={`${DATA_SOURCES_TEST_PREFIX}-merge-dupes-toggle`}
        label={appstr("Merge duplicates")}
        size={"medium"}
        checked={effectiveMergeDupes}
        onChange={() =>
          onChange({
            mergeDupes: !effectiveMergeDupes,
            mergeByField: undefined
          })
        }
      />
      {effectiveMergeDupes && (
        <Select
          dataTestId={`${DATA_SOURCES_TEST_PREFIX}-merge-by-field-select`}
          label={appstr("Merge by field") + ":"}
          inputOnly={false}
          selectSize={"medium"}
          disabled={options.length === 0}
          options={options}
          value={effectiveMergeByField}
          listProps={{ enableSearch: options.length > 15 }}
          placeholder={effectiveMergeByField ?? appstr("Select field")}
          listWidth={"medium"}
          position={"right"}
        />
      )}
    </>
  );
};

export const SourceMapComposite = ({
  config,
  chartConfig,
  importType,
  index,
  idKey = "PersonID",
  superIDkey = "SupervisorID",
  onChange,
  ATSType = enumATSModes.notATS,
  showLoadHeaders,
  showBasicConfig = true,
  showMergeDupesConfig = true
}) => {
  const [loadedHeaders, setLoadedHeaders] = React.useState(config.headers ?? []);
  const isPositionChart = IsPositionChart(chartConfig);

  const positionChartFields = isPositionChart
    ? getFieldKeysForPositionChart(config, importType)
    : null;

  const loadHeaders = async () => {
    let isAutoLoad = seccondaryAutoLoadConnectors.includes(config.source);
    let mapping = isAutoLoad ? GetMapping(config.source, chartConfig) : false;

    if (!mapping && !kATSConnectorsWithStaticMapping.includes(config.source)) {
      let options = { connectionOptions: config, type: importType, index };
      const { response } = await LoadHeadersRFC(chartConfig, options);

      if (!response["success"]) return { success: false };
      config.headers = response["headers"];
      mapping = response["mapping"];
    } else {
      config.headers = [...new Set(Object.values(mapping))];
    }
    const { newChartObj, changedSections } = AttachLoadedHeadersToChart(
      { headers: config.headers, mapping: mapping },
      chartConfig,
      importType,
      index
    );

    setLoadedHeaders(config.headers);
    onChange({ headers: config.headers });
  };

  if (importType === "merge") {
    idKey = "recordField";
    superIDkey = "mergeField";
  } else if (positionChartFields) {
    idKey = positionChartFields.idKey;
    superIDkey = positionChartFields.superIDkey;
  }

  let personMap = GetSourceMap(chartConfig, config, idKey);
  let superMap = GetSourceMap(chartConfig, config, superIDkey);

  const { ATS, SeparateJobReqs, ATSMap, SeparateJobReqsCountField, myMap } = config;
  // backward compatibility: fallback to full headers for existing charts that haven't been refreshed since new data structure
  const mainHeaders = chartConfig.import.headers ?? chartConfig.headers ?? [];
  const ATSMode =
    ATSType === enumATSModes.pureATS ||
    (ATSType === enumATSModes.toggleATS && config.ATS && !isPositionChart) ||
    ATSType === enumATSModes.nonConfigurableATS;

  const [personMapState, setPersonMapState] = React.useState(personMap);
  const [superMapState, setSuperMapState] = React.useState(superMap);

  const onMapChange = () => {
    let cleanedMap = { ...config.map };

    // Clean up conflicting fields for position charts
    if (positionChartFields?.fieldsToRemove) {
      positionChartFields.fieldsToRemove.forEach(field => {
        delete cleanedMap[field];
      });
    }

    cleanedMap[idKey] = personMap;
    cleanedMap[superIDkey] = superMap;

    onChange({ map: cleanedMap });
  };

  const showDisplayFieldMapping =
    importType === "append" && kAppendDisplayFieldMappingConnectors.includes(config.source);
  const displayFields = GetAppendDisplayFields(
    isPositionChart ? GetAppendDataType(config) : "employees"
  );
  const displayFieldMap = config.map ?? {};

  const setDisplayField = (fieldKey, header) => {
    onChange({ map: { ...config.map, [fieldKey]: header } });
  };

  const displayFieldOptions = fieldKey =>
    createSelectOptions(loadedHeaders, header => setDisplayField(fieldKey, header));

  const getRecordFieldHeaders = () => {
    return importType === "merge" ? mainHeaders : loadedHeaders;
  };

  const personHeaderOptions = createSelectOptions(getRecordFieldHeaders(), header => {
    personMap = header;
    setPersonMapState(personMap);
    onMapChange();
  });

  const superHeaderOptions = createSelectOptions(loadedHeaders, header => {
    superMap = header;
    setSuperMapState(superMap);
    onMapChange();
  });

  const myMapHeaderOptions = createSelectOptions(mainHeaders, header => {
    //What field in my append dataset corresponds to the req email?
    onChange({ myMap: header });
  });

  const ATSMapHeaderOptions = createSelectOptions(loadedHeaders, header => {
    //What field in my main dataset corresponds to the hiring manager's email?
    onChange({ ATSMap: header });
  });

  const separateReqFieldOptions = createSelectOptions(loadedHeaders, header => {
    onChange({ SeparateJobReqsCountField: header });
  });

  const shouldShowATS = ATSType === enumATSModes.toggleATS && !isPositionChart;

  return (
    <Flex key={index} direction={"column"} gap={"12px"}>
      <Text size={"medium"} modifier={"medium"}>
        {appstr("Configuration")}
      </Text>
      {showBasicConfig && (
        <>
          {shouldShowATS && (
            <Checkbox
              id={"toggle-ats"}
              data-testid={`${DATA_SOURCES_TEST_PREFIX}-ats-toggle`}
              label={appcstr("ATS")}
              size={"medium"}
              checked={ATS}
              onChange={() => onChange({ ...config, ATS: !ATS })}
            />
          )}
          {!ATSMode ? (
            <Flex direction={"column"}>
              <Select
                dataTestId={fieldSelectTestId(idKey)}
                label={appstr(idKey)}
                inputOnly={false}
                selectSize={"medium"}
                disabled={personHeaderOptions.length === 0}
                options={personHeaderOptions}
                value={personMapState}
                placeholder={
                  personMapState && personMapState.length > 0
                    ? personMapState
                    : appstr("Select field")
                }
                listProps={{ enableSearch: personHeaderOptions.length > 15 }}
                listWidth={"medium"}
                position={"right"}
              />
              <Select
                dataTestId={fieldSelectTestId(superIDkey)}
                label={appstr(superIDkey)}
                inputOnly={false}
                selectSize={"medium"}
                disabled={superHeaderOptions.length === 0}
                options={superHeaderOptions}
                listProps={{ enableSearch: superHeaderOptions.length > 15 }}
                value={superMapState}
                placeholder={
                  superMapState && superMapState.length > 0 ? superMapState : appstr("Select field")
                }
                listWidth={"medium"}
                position={"right"}
              />
              {showDisplayFieldMapping &&
                displayFields.map(fieldKey => {
                  const options = displayFieldOptions(fieldKey);
                  const value = displayFieldMap[fieldKey] ?? "";
                  return (
                    <Select
                      key={fieldKey}
                      dataTestId={fieldSelectTestId(fieldKey)}
                      label={appstr(fieldKey)}
                      inputOnly={false}
                      selectSize={"medium"}
                      disabled={options.length === 0}
                      options={options}
                      value={value}
                      listProps={{ enableSearch: options.length > 15 }}
                      placeholder={value.length > 0 ? value : appstr("Select field")}
                      listWidth={"medium"}
                      position={"right"}
                    />
                  );
                })}
            </Flex>
          ) : (
            <Flex direction={"column"} gap={"8px"}>
              {ATSType === enumATSModes.toggleATS && (
                <Select
                  dataTestId={`${DATA_SOURCES_TEST_PREFIX}-hiring-manager-select`}
                  label={appcstr("Hiring Manager") + ":"}
                  inputOnly={false}
                  selectSize={"medium"}
                  disabled={ATSMapHeaderOptions.length === 0}
                  options={ATSMapHeaderOptions}
                  value={ATSMap}
                  listProps={{ enableSearch: ATSMapHeaderOptions.length > 15 }}
                  placeholder={ATSMap && ATSMap.length > 0 ? ATSMap : appstr("Select field")}
                  listWidth={"medium"}
                  position={"right"}
                />
              )}
              {ATSType !== enumATSModes.nonConfigurableATS && (
                <Select
                  dataTestId={`${DATA_SOURCES_TEST_PREFIX}-ats-email-select`}
                  label={appcstr("Email Field") + ":"}
                  inputOnly={false}
                  selectSize={"medium"}
                  disabled={myMapHeaderOptions.length === 0}
                  options={myMapHeaderOptions}
                  value={myMap}
                  listProps={{ enableSearch: myMapHeaderOptions.length > 15 }}
                  placeholder={myMap && myMap.length > 0 ? myMap : appstr("Select field")}
                  listWidth={"medium"}
                  position={"right"}
                />
              )}
              {!isPositionChart && (
                <Checkbox
                  id={"toggle-separatereq"}
                  data-testid={`${DATA_SOURCES_TEST_PREFIX}-separate-job-reqs-toggle`}
                  label={appcstr("Separate Job Reqs")}
                  size={"medium"}
                  checked={SeparateJobReqs}
                  onChange={() => onChange({ ...config, SeparateJobReqs: !SeparateJobReqs })}
                />
              )}
              {ATSMode &&
                SeparateJobReqs &&
                ![enumATSModes.pureATS, enumATSModes.nonConfigurableATS].includes(ATSType) && (
                  /// pureATS + nonConfigurableATS === 'ICIMS', 'ADP Recruitment', 'ADP Recruit Management', "ADP WFN CA Recruitment Module", 'Greenhouse', 'Lever', 'Jobvite'
                  <Select
                    dataTestId={`${DATA_SOURCES_TEST_PREFIX}-req-count-select`}
                    label={appcstr("Req Count Field1") + ":"}
                    inputOnly={false}
                    selectSize={"medium"}
                    disabled={separateReqFieldOptions.length === 0}
                    options={separateReqFieldOptions}
                    value={SeparateJobReqsCountField}
                    listProps={{ enableSearch: separateReqFieldOptions.length > 15 }}
                    placeholder={
                      SeparateJobReqsCountField && SeparateJobReqsCountField.length > 0
                        ? SeparateJobReqsCountField
                        : appcstr("Req Count Field2")
                    }
                    listWidth={"medium"}
                    position={"right"}
                  />
                )}
            </Flex>
          )}
        </>
      )}
      {showMergeDupesConfig && !isPositionChart && (
        <MergeDupesSection
          config={config}
          headers={loadedHeaders}
          onChange={onChange}
          id={`toggle-merge-dupes-${index ?? "main"}`}
        />
      )}
      {showLoadHeaders && (
        <span>
          <Button
            dataTestId={`${DATA_SOURCES_TEST_PREFIX}-load-map-button`}
            onClick={loadHeaders}
            modifier={"secondary"}
            size={"medium"}
            iconProps={{ name: "arrows-rotate" }}
          >
            {appstr("LoadMap")}
          </Button>
        </span>
      )}
    </Flex>
  );
};
